import React, { useState, useEffect, useCallback } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";

type Status = "pass" | "fail" | "info" | "pending";

interface Check {
  label: string;
  status: Status;
  value: string;
}

interface CheckGroup {
  title: string;
  checks: Check[];
}

const statusColor: Record<Status, string> = {
  pass: "text-green-400",
  fail: "text-red-400",
  info: "text-blue-300",
  pending: "text-yellow-400",
};

const statusIcon: Record<Status, string> = {
  pass: "✅",
  fail: "❌",
  info: "ℹ️",
  pending: "⏳",
};

function detectPlatform(): string {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

async function fetchStatus(url: string): Promise<{ status: number; contentType: string; redirected: boolean }> {
  try {
    const r = await fetch(url, { cache: "no-store" });
    return { status: r.status, contentType: r.headers.get("content-type") || "?", redirected: r.redirected };
  } catch {
    return { status: -1, contentType: "fetch error", redirected: false };
  }
}

async function fetchRedirectCheck(url: string): Promise<{ status: number; redirected: boolean }> {
  try {
    const r = await fetch(url, { redirect: "manual", cache: "no-store" });
    return { status: r.status, redirected: r.type === "opaqueredirect" || (r.status >= 300 && r.status < 400) };
  } catch {
    return { status: -1, redirected: false };
  }
}

async function measureImage(url: string): Promise<{ w: number; h: number; ok: boolean }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight, ok: true });
    img.onerror = () => resolve({ w: 0, h: 0, ok: false });
    img.src = url + "?_t=" + Date.now();
  });
}

async function crossDomainCheck(url: string): Promise<string> {
  try {
    const r = await fetch(url, { mode: "no-cors", cache: "no-store" });
    return `opaque (type=${r.type})`;
  } catch (e: any) {
    return `error: ${e.message}`;
  }
}

export function PWADebugPanel() {
  const [open, setOpen] = useState(true);
  const [groups, setGroups] = useState<CheckGroup[]>([]);
  const [bipFired, setBipFired] = useState(false);
  const [bipTimestamp, setBipTimestamp] = useState<string | null>(null);

  const runDiagnostics = useCallback(async () => {
    const result: CheckGroup[] = [];

    // ── Page / Origin ──
    const platform = detectPlatform();
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    const displayMode = isStandalone ? "standalone" : window.matchMedia("(display-mode: minimal-ui)").matches ? "minimal-ui" : "browser";
    const navStandalone = (navigator as any).standalone;

    result.push({
      title: "📄 Página / Origem",
      checks: [
        { label: "URL atual", status: "info", value: location.href },
        { label: "Origem", status: "info", value: location.origin },
        { label: "document.referrer", status: "info", value: document.referrer || "(vazio)" },
        { label: "User agent", status: "info", value: navigator.userAgent },
        { label: "Plataforma", status: "info", value: platform },
        { label: "Data/hora sessão", status: "info", value: new Date().toISOString() },
      ],
    });

    // ── Manifest ──
    const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    const manifestHref = manifestLink?.href || "(não encontrado)";
    const manifestChecks: Check[] = [
      { label: "Link no DOM", status: manifestLink ? "pass" : "fail", value: manifestLink?.getAttribute("href") || "(ausente)" },
      { label: "Href resolvido", status: manifestLink ? "info" : "fail", value: manifestHref },
    ];

    let manifestData: any = null;
    if (manifestLink) {
      const mf = await fetchStatus(manifestHref);
      manifestChecks.push({
        label: "Fetch manifest",
        status: mf.status === 200 ? "pass" : "fail",
        value: `status=${mf.status} type=${mf.contentType} redirected=${mf.redirected}`,
      });

      if (mf.status === 200) {
        try {
          const r = await fetch(manifestHref, { cache: "no-store" });
          manifestData = await r.json();
        } catch { /* ignore */ }
      }

      if (manifestData) {
        manifestChecks.push(
          { label: "manifest.id", status: manifestData.id ? "pass" : "fail", value: String(manifestData.id ?? "(ausente)") },
          { label: "manifest.scope", status: manifestData.scope ? "pass" : "fail", value: String(manifestData.scope ?? "(ausente)") },
          { label: "manifest.start_url", status: manifestData.start_url ? "pass" : "fail", value: String(manifestData.start_url ?? "(ausente)") },
          { label: "manifest.display", status: manifestData.display === "standalone" ? "pass" : "fail", value: String(manifestData.display ?? "(ausente)") },
          {
            label: "prefer_related_applications",
            status: manifestData.prefer_related_applications === false ? "pass" : manifestData.prefer_related_applications === undefined ? "info" : "fail",
            value: String(manifestData.prefer_related_applications ?? "(ausente)"),
          },
        );

        // start_url same origin
        try {
          const resolved = new URL(manifestData.start_url, location.origin);
          manifestChecks.push({
            label: "start_url mesma origem",
            status: resolved.origin === location.origin ? "pass" : "fail",
            value: resolved.href,
          });
        } catch {
          manifestChecks.push({ label: "start_url mesma origem", status: "fail", value: "URL inválida" });
        }
      }
    }

    // Redirect checks
    const rootRedirect = await fetchRedirectCheck(location.origin + "/");
    manifestChecks.push({
      label: "Redirect em /",
      status: rootRedirect.redirected ? "fail" : "pass",
      value: `status=${rootRedirect.status} redirected=${rootRedirect.redirected}`,
    });

    if (manifestData?.start_url) {
      const suRedirect = await fetchRedirectCheck(new URL(manifestData.start_url, location.origin).href);
      manifestChecks.push({
        label: "Redirect em start_url",
        status: suRedirect.redirected ? "fail" : "pass",
        value: `status=${suRedirect.status} redirected=${suRedirect.redirected}`,
      });
    }

    // /manifest.json probe
    const mjson = await fetchStatus(location.origin + "/manifest.json");
    manifestChecks.push({
      label: "/manifest.json probe",
      status: "info",
      value: `status=${mjson.status} type=${mjson.contentType}`,
    });

    result.push({ title: "📋 Manifest", checks: manifestChecks });

    // ── Icons ──
    const iconChecks: Check[] = [];
    for (const size of [192, 512]) {
      const url = `${location.origin}/icons/icon-${size}x${size}.png`;
      const fs = await fetchStatus(url);
      const img = await measureImage(url);
      const sizeMatch = img.ok && img.w === size && img.h === size;
      iconChecks.push({
        label: `icon-${size}`,
        status: fs.status === 200 && sizeMatch ? "pass" : "fail",
        value: `fetch=${fs.status} type=${fs.contentType} real=${img.w}x${img.h}`,
      });
    }
    result.push({ title: "🖼️ Ícones", checks: iconChecks });

    // ── Service Worker ──
    const swChecks: Check[] = [];
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      swChecks.push({
        label: "SW registrado",
        status: reg ? "pass" : "fail",
        value: reg ? `scope=${reg.scope} script=${reg.active?.scriptURL || reg.installing?.scriptURL || "?"}` : "(nenhum)",
      });

      const ctrl = navigator.serviceWorker.controller;
      swChecks.push({
        label: "SW controlando página",
        status: ctrl ? "pass" : "fail",
        value: ctrl ? ctrl.scriptURL : "(sem controller)",
      });

      if (reg) {
        const swScope = reg.scope;
        const pageInSwScope = location.href.startsWith(swScope);
        swChecks.push({
          label: "Página no scope do SW",
          status: pageInSwScope ? "pass" : "fail",
          value: `page=${location.href} scope=${swScope}`,
        });

        if (manifestData?.scope) {
          const manifestScope = new URL(manifestData.scope, location.origin).href;
          const pageInManifestScope = location.href.startsWith(manifestScope);
          swChecks.push({
            label: "Página no scope do manifest",
            status: pageInManifestScope ? "pass" : "fail",
            value: `manifestScope=${manifestScope}`,
          });
          swChecks.push({
            label: "Scopes coincidem",
            status: swScope === manifestScope ? "pass" : "fail",
            value: `sw=${swScope} manifest=${manifestScope}`,
          });
        }
      }

      try {
        const ready = await Promise.race([
          navigator.serviceWorker.ready.then(() => true),
          new Promise<false>((r) => setTimeout(() => r(false), 5000)),
        ]);
        swChecks.push({
          label: "SW ready resolvido",
          status: ready ? "pass" : "fail",
          value: ready ? "OK" : "timeout 5s",
        });
      } catch {
        swChecks.push({ label: "SW ready resolvido", status: "fail", value: "erro" });
      }
    } else {
      swChecks.push({ label: "SW suportado", status: "fail", value: "navigator.serviceWorker não existe" });
    }
    result.push({ title: "⚙️ Service Worker", checks: swChecks });

    // ── Installability ──
    const bipReceived = !!(window as any).__pwaDiagBipFired;
    const bipTs = (window as any).__pwaDiagBipTimestamp || null;
    if (bipReceived) {
      setBipFired(true);
      setBipTimestamp(bipTs);
    }

    const dismissed = localStorage.getItem("pwa-install-dismissed-at");
    result.push({
      title: "📲 Instalabilidade",
      checks: [
        { label: "beforeinstallprompt", status: bipReceived || bipFired ? "pass" : "fail", value: bipReceived || bipFired ? `recebido em ${bipTimestamp || bipTs}` : "NÃO recebido" },
        { label: "Display-mode", status: "info", value: displayMode },
        { label: "Já standalone", status: isStandalone ? "info" : "info", value: isStandalone ? "SIM" : "NÃO" },
        { label: "navigator.standalone (iOS)", status: "info", value: String(navStandalone ?? "undefined") },
        { label: "pwa-install-dismissed-at", status: dismissed ? "info" : "info", value: dismissed || "(não definido)" },
      ],
    });

    // ── Cross-domain comparison ──
    const domains = ["https://app2.forjahub.com"];
    const crossChecks: Check[] = [];
    for (const d of domains) {
      const r = await crossDomainCheck(d + "/manifest.webmanifest");
      crossChecks.push({ label: d, status: "info", value: r });
    }
    result.push({ title: "🌐 Comparação de Domínios", checks: crossChecks });

    setGroups(result);
  }, [bipFired, bipTimestamp]);

  useEffect(() => {
    runDiagnostics();

    // Listen for late beforeinstallprompt
    const handler = () => {
      setBipFired(true);
      setBipTimestamp(new Date().toISOString());
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Poll for flag from main.tsx
    const interval = setInterval(() => {
      if ((window as any).__pwaDiagBipFired && !bipFired) {
        setBipFired(true);
        setBipTimestamp((window as any).__pwaDiagBipTimestamp || new Date().toISOString());
      }
    }, 2000);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      clearInterval(interval);
    };
  }, []);

  // Re-run when bipFired changes
  useEffect(() => {
    if (bipFired) runDiagnostics();
  }, [bipFired, runDiagnostics]);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-[99999] w-12 h-12 rounded-full bg-black/90 text-white flex items-center justify-center text-xl shadow-lg border border-white/20"
      >
        🔍
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[99999] bg-black/95 text-white flex flex-col">
      <div className="flex items-center justify-between p-3 border-b border-white/10">
        <h2 className="text-sm font-bold">🔧 PWA Debug Panel</h2>
        <div className="flex gap-2">
          <button onClick={() => runDiagnostics()} className="text-xs px-2 py-1 bg-white/10 rounded">🔄 Re-run</button>
          <button onClick={() => setOpen(false)} className="text-xs px-2 py-1 bg-white/10 rounded">✕</button>
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-4 text-xs">
          {groups.map((g, gi) => (
            <div key={gi}>
              <h3 className="font-bold text-sm mb-1">{g.title}</h3>
              <div className="space-y-1">
                {g.checks.map((c, ci) => (
                  <div key={ci} className="flex gap-2 items-start">
                    <span>{statusIcon[c.status]}</span>
                    <span className="font-medium whitespace-nowrap">{c.label}:</span>
                    <span className={`${statusColor[c.status]} break-all`}>{c.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {groups.length === 0 && <p className="text-yellow-400">Executando diagnóstico…</p>}
        </div>
      </ScrollArea>
    </div>
  );
}
